import {useEffect} from 'react';
import {NavLink, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {CalendarDots, ChartBar, Images, Gear, Scissors, SignOut, ListChecks} from '@phosphor-icons/react';
import {VStack} from '@astryxdesign/core/VStack';
import {AsyncBoundary} from '@/components/AsyncBoundary';
import {useAuth} from '@/hooks/useAuth';
import {OwnerSessionProvider} from '@/app/OwnerSessionContext';
import {AssistantSheet} from '@/components/assistant/AssistantSheet';
import {ownerPath} from '@/lib/tenant/resolve';
import {cn} from '@/lib/utils';

const TABS = [
  {to: 'schedule', label: 'Расписание', icon: CalendarDots},
  {to: 'bookings', label: 'Записи', icon: ListChecks},
  {to: 'services', label: 'Услуги', icon: Scissors},
  {to: 'media', label: 'Фото', icon: Images},
  {to: 'stats', label: 'Итоги', icon: ChartBar},
  {to: 'settings', label: 'Настройки', icon: Gear},
] as const;

export function OwnerLayout() {
  const {slug = ''} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {session, isLoading, signOut} = useAuth();
  const isLogin = location.pathname.endsWith('/login');

  useEffect(() => {
    if (!isLoading && !session && !isLogin) {
      void navigate(ownerPath(slug, 'login'), {replace: true});
    }
  }, [isLoading, session, isLogin, navigate, slug]);

  if (isLogin) return <Outlet />;

  if (isLoading || !session) {
    return (
      <VStack minHeight="100dvh" padding={4}>
        <AsyncBoundary isLoading={isLoading} />
      </VStack>
    );
  }

  return (
    <OwnerSessionProvider value={{slug, session}}>
      <div className="owner-area flex min-h-dvh flex-col pb-nav">
        <header className="flex flex-col gap-4 px-4 pb-3 pt-5">
          <div className="flex flex-col gap-3">
            <div className="flex min-w-0 flex-col gap-1">
              <h1 className="m-0 text-[28px] font-bold leading-tight tracking-tight text-foreground">Кабинет студии</h1>
              <span className="truncate text-[14px] text-muted-foreground">{session.user.email}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <AssistantSheet slug={slug} scope="owner" accessToken={session.access_token} />
              <button
                type="button"
                onClick={() => void signOut()}
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[14px] font-semibold text-foreground"
              >
                <SignOut size={18} aria-hidden />
                Выйти
              </button>
            </div>
          </div>
          <nav aria-label="Разделы кабинета" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.to}
                  to={ownerPath(slug, tab.to)}
                  className={({isActive}) =>
                    cn(
                      'flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-[14px] font-semibold transition-colors',
                      isActive ? 'border-transparent bg-white/15 text-foreground' : 'border-white/10 bg-[#111113] text-muted-foreground',
                    )
                  }
                >
                  <Icon size={18} weight="fill" aria-hidden />
                  {tab.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="h-px bg-white/10" />
        </header>
        <div key={location.pathname} className="page-enter flex flex-1 flex-col">
          <div className="stagger flex flex-col gap-4 px-4 pb-8">
            <Outlet />
          </div>
        </div>
      </div>
    </OwnerSessionProvider>
  );
}
